import React, { useEffect, useMemo, useState, useCallback } from "react";
import "../styles/QuoteBuilder.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../App.css";

const currency = (n) => `€${Number(n || 0).toFixed(2)}`;

export default function QuoteBuilder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("id");
  const isEdit = !!editId;

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({ iva_percent: 21 });

  const [selectedCustomer, setSelectedCustomer] = useState("");
  // unit_price stays on each item (we keep it synced to IVA toggle)
  const [selectedItems, setSelectedItems] = useState([
    { product_id: "", quantity: 1, unit_price: null },
  ]);
  const [discount, setDiscount] = useState(0);
  const [type, setType] = useState(1); // 1 = no IVA (use price2), 2 = with IVA (use price)

  // Notice modal (popup)
  const [notice, setNotice] = useState(null); // { text, error? }
  const [navAfterClose, setNavAfterClose] = useState(false);
  const closeNotice = () => {
    setNotice(null);
    if (navAfterClose) {
      setNavAfterClose(false);
      navigate("/quotes");
    }
  };

  const ivaPercent = Number(settings?.iva_percent ?? 21);

  // Load customers, products, settings
  useEffect(() => {
    fetch("/api/customers").then(r => r.json()).then(setCustomers).catch(() => setCustomers([]));
    fetch("/api/products")
      .then(r => r.json())
      .then(data => setProducts(Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])))
      .catch(() => setProducts([]));
    fetch("/api/settings").then(r => r.json()).then(setSettings).catch(() => setSettings({ iva_percent: 21 }));
  }, []);

  // Helper: find product
  const getProduct = useCallback(
    (id) => products.find((p) => Number(p.id) === Number(id)),
    [products]
  );

  // Helper: pick the correct field based on IVA toggle
  const pickUnitFromProduct = useCallback(
    (prod) => {
      if (!prod) return 0;
      return Number(type === 2 ? prod.price : prod.price2) || 0;
    },
    [type]
  );

  // Load quote for editing
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await fetch(`/api/quotes/${editId}`);
        if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
        const q = await res.json();

        setSelectedCustomer(String(q.customer_id || ""));
        setDiscount(Number(q.discount || 0));
        setType(Number(q.type || 1));

        let items = [];
        try { items = Array.isArray(q.items) ? q.items : JSON.parse(q.items || "[]"); }
        catch { items = []; }
        if (!items.length) items = [{ product_id: "", quantity: 1, unit_price: null }];

        // Keep any saved snapshot, otherwise we will fill from catalogue below
        setSelectedItems(
          items.map(i => ({
            product_id: String(i.product_id || ""),
            quantity: Number(i.quantity || 1),
            unit_price: (i.unit_price === 0 || typeof i.unit_price === "number")
              ? Number(i.unit_price)
              : null,
          }))
        );
      } catch (e) {
        console.error(e);
        setNotice({ text: "Failed to load quote for editing.", error: true });
      }
    })();
  }, [isEdit, editId]);

  // When IVA toggle changes, (re)fill each line's unit_price from the correct product field
  useEffect(() => {
    setSelectedItems(prev => prev.map(i => {
      const p = getProduct(i.product_id);
      // Always sync to the correct field so totals + PDF match business rule
      return { ...i, unit_price: pickUnitFromProduct(p) };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, products]);

  // Totals (based on line snapshots)
  const { netBeforeIVA, ivaAmount, finalTotal } = useMemo(() => {
    const grossSum = selectedItems.reduce((sum, item) => {
      const unit = (item.unit_price === 0 || typeof item.unit_price === "number")
        ? Number(item.unit_price)
        : 0;
      const qty = Number(item.quantity) || 0;
      return sum + unit * qty;
    }, 0);

    const discountAmt = Number(discount) || 0;
    const net = Math.max(0, grossSum - discountAmt);
    const iva = type === 2 ? Number((net * ivaPercent / 100).toFixed(2)) : 0;
    const final = Number((net + iva).toFixed(2));

    return {
      netBeforeIVA: Number(net.toFixed(2)),
      ivaAmount: iva,
      finalTotal: final,
    };
  }, [selectedItems, discount, type, ivaPercent]);

  const handleAddItem = () => {
    setSelectedItems(prev => [...prev, { product_id: "", quantity: 1, unit_price: null }]);
  };

  const handleItemChange = (index, field, value) => {
    setSelectedItems(prev => {
      const copy = [...prev];
      if (field === "product_id") {
        const pid = value;
        const prod = getProduct(pid);
        copy[index] = {
          ...copy[index],
          product_id: pid,
          unit_price: pickUnitFromProduct(prod),
        };
      } else if (field === "quantity") {
        copy[index] = { ...copy[index], quantity: Number(value) };
      } else if (field === "unit_price") {
        copy[index] = { ...copy[index], unit_price: value === "" ? null : Number(value) };
      } else {
        copy[index] = { ...copy[index], [field]: value };
      }
      return copy;
    });
  };

  const handleRemoveItem = (index) => {
    setSelectedItems(prev => prev.filter((_, i) => i !== index));
  };

  // Button to (re)fill unit prices from catalogue for all lines
  const fillAllUnitPrices = () => {
    setSelectedItems(prev => prev.map(i => ({
      ...i,
      unit_price: pickUnitFromProduct(getProduct(i.product_id)),
    })));
  };

  const hasAnyLine = selectedItems.length > 0;

  // --- Saving helpers ---
  async function saveQuote({ reprice = false } = {}) {
    if (!selectedCustomer) {
      setNotice({ text: "Please select a customer.", error: true });
      return;
    }
    const cleanedItems = selectedItems
      .filter((i) => i.product_id && Number(i.quantity) > 0)
      .map((i) => ({
        product_id: String(i.product_id),
        quantity: Number(i.quantity),
        unit_price:
          (i.unit_price === 0 || typeof i.unit_price === "number")
            ? Number(i.unit_price)
            : 0,
      }));

    if (cleanedItems.length === 0) {
      setNotice({ text: "Please add at least one product/service.", error: true });
      return;
    }

    const payload = {
      customer_id: String(selectedCustomer),
      items: cleanedItems,
      discount: Number(discount) || 0,
      type, // 2=IVA (use price); 1=no IVA (use price2)
      ...(reprice ? { reprice: true } : {}),
    };

    try {
      const url = isEdit ? `/api/quotes/${editId}${reprice ? "?reprice=1" : ""}` : "/api/quotes";
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);

      setNotice({ text: isEdit ? "Quote updated." : `Quote created: ${data.quote_number}` });
      setNavAfterClose(true);
    } catch (err) {
      console.error(err);
      setNotice({ text: isEdit ? "Error updating quote." : "Error saving quote.", error: true });
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    saveQuote({ reprice: false });
  };
  const handleRepriceAndSave = (e) => {
    e.preventDefault();
    // ensure we sync all unit prices to current catalogue + IVA mode before "reprice"
    fillAllUnitPrices();
    saveQuote({ reprice: true });
  };

  return (
    <div className="container">
      <div className="card">
        <h2 className="section-title">{isEdit ? "Edit Quote" : "Quote Builder"}</h2>

        {/* Customer */}
        <div className="mb-4">
          <div className="label">Select Customer</div>
          <select
            className="select"
            value={selectedCustomer}
            onChange={(e) => setSelectedCustomer(e.target.value)}
          >
            <option value="">-- Select Customer --</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.first_name} {c.surname} {c.email ? `(${c.email})` : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Products / Services */}
        <div className="mb-3">
          <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <div className="label" style={{ margin: 0 }}>Products / Services</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button type="button" onClick={handleAddItem} className="btn secondary">
                + Add Product/Service
              </button>
              {hasAnyLine && (
                <button type="button" className="btn secondary" onClick={fillAllUnitPrices}>
                  Fill prices from catalogue
                </button>
              )}
            </div>
          </div>

          {selectedItems.map((item, idx) => (
            <div key={idx} className="row mt-2" style={{ alignItems: "center" }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div className="label">Product</div>
                <select
                  className="input"
                  value={item.product_id}
                  onChange={(e) => handleItemChange(idx, "product_id", e.target.value)}
                >
                  <option value="">-- Select Product --</option>
                  {products.map((prod) => (
                    <option key={prod.id} value={prod.id}>
                      {prod.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ width: 120 }}>
                <div className="label">Qty</div>
                <input
                  type="number"
                  min="1"
                  className="input"
                  value={item.quantity}
                  onChange={(e) => handleItemChange(idx, "quantity", e.target.value)}
                />
              </div>

              <div style={{ width: 170 }}>
                <div className="label">Unit Price (€)</div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="input"
                  value={item.unit_price ?? ""}
                  placeholder={item.unit_price == null ? "not set" : undefined}
                  onChange={(e) => handleItemChange(idx, "unit_price", e.target.value)}
                />
              </div>

              <div style={{ width: 170, textAlign: "right" }}>
                <div className="label">Line Total</div>
                <div>
                  <strong>
                    {currency((Number(item.unit_price || 0)) * (Number(item.quantity) || 0))}
                  </strong>
                </div>
              </div>

              <div style={{ width: 100, textAlign: "right" }}>
                <button type="button" onClick={() => handleRemoveItem(idx)} className="btn danger">
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Discount + IVA */}
        <div className="form-grid mt-3">
          <div>
            <div className="label">Discount (€)</div>
            <input
              type="number"
              step="0.01"
              min="0"
              className="input"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
            />
          </div>

          <div className="row" style={{ alignItems: "flex-end" }}>
            <label className="row" style={{ alignItems: "center", gap: 10 }}>
              <input
                type="checkbox"
                checked={type === 2}
                onChange={(e) => setType(e.target.checked ? 2 : 1)}
              />
              <span>Include IVA ({ivaPercent}%)</span>
            </label>
          </div>
        </div>

        {/* Totals */}
        <div className="card mt-4">
          <div className="right">
            {Number(discount) > 0 && (
              <div className="muted">
                Discount: <strong>{currency(discount)}</strong>
              </div>
            )}
            <div>
              Net total: <strong>{currency(netBeforeIVA)}</strong>
            </div>
            {type === 2 && (
              <div>
                IVA ({ivaPercent}%): <strong>{currency(ivaAmount)}</strong>
              </div>
            )}
            <div
              className="mt-3"
              style={{ borderTop: "1px solid var(--border)", paddingTop: 10, fontSize: 18 }}
            >
              Subtotal: <strong>{currency(finalTotal)}</strong>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div
          className="mt-4"
          style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}
        >
          <button
            type="button"
            className="btn cancel"
            onClick={() => navigate("/quotes")}
          >
            Cancel
          </button>

          {isEdit && (
            <button type="button" className="btn secondary" onClick={handleRepriceAndSave}>
              Update Prices &amp; Save
            </button>
          )}

          <button onClick={handleSubmit} className="btn">
            {isEdit ? "Save Changes" : "Save Quote"}
          </button>
        </div>
      </div>

      {/* Notice modal */}
      {notice && (
        <div className="qp-modal is-open" role="dialog" aria-modal="true" aria-labelledby="notice-title">
          <div
            className="qp-modal__overlay"
            onClick={closeNotice}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)" }}
          />
          <div
            className="qp-modal__dialog"
            style={{
              position: "fixed",
              left: "50%",
              top: "20%",
              transform: "translateX(-50%)",
              background: "#fff",
              padding: 16,
              borderRadius: 12,
              width: "min(520px, 90vw)",
              boxShadow: "0 10px 30px rgba(0,0,0,.25)",
            }}
          >
            <h3 id="notice-title" style={{ margin: "0 0 8px" }}>
              {notice.error ? "Oops" : "Success"}
            </h3>
            <p style={{ margin: "0 0 14px" }}>{notice.text}</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="btn" onClick={closeNotice}>OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

